import { THREE_KERNEL as THREE } from './threeKernel.js';
import { Entity } from '../domain/entity.js';
import { InventorySystem } from '../domain/items/inventorySystem.js';
import { FloatingTextUI } from './controllers/floatingTextUI.js';
import { ShopUI } from './controllers/shopUI.js';
import { createHudFromDom } from './controllers/createHudFromDom.js';
import { InputController } from './controllers/inputController.js';
import { createRecallInputWire } from './controllers/recallInputWire.js';
import { createShopToggleHandler } from './controllers/shopInputWire.js';
import { MeshFactory } from './factory/meshFactory.js';
import { ChampionMeshFactory } from './factory/championMeshFactory.js';
import './championVisualBoot.js';
import '../application/championKitBoot.js';
import { getMapConfig } from '../domain/maps.js';
import { getDomElements } from './domElements.js';
import { MatchRuntime } from '../application/matchRuntime.js';
import { bootSandboxStructures } from './bootSandboxStructures.js';
import { bootDebugTools } from './bootDebugTools.js';
import { createThreeSceneManager } from './scene/threeSceneManager.js';
import { createBuffAuraGroup } from './scene/entitySceneManager.js';
import { createEntityInspectController } from './controllers/entityInspectController.js';
import { createSwitchChampionController } from './controllers/switchChampionController.js';
import { createPlayerAbilityCastController } from './controllers/playerAbilityCastController.js';
import {
  createAbilityCasterVfx,
  wireGroundZoneOpacityDebug
} from './vfx/abilityCasterVfx.js';
import { bootAbilitySfx } from './bootAbilitySfx.js';
import { wireAbilityCastDebug } from './controllers/wireAbilityCastDebug.js';
import {
  bootSandboxPropGroups,
  createJungleSpawnHandler
} from './controllers/sandboxMapExtrasBoot.js';
import { bootMapLobbyLoop } from './controllers/bootMapLobbyLoop.js';
import { bootSummonerSpells } from './bootSummonerSpells.js';

const {
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  Plane,
  RingGeometry,
  Vector3
} = THREE;

// Bootstrapping Container & Scene Setup
const dom = getDomElements();
const { scene, camera, renderer, gridHelper } = createThreeSceneManager(dom.container);

// State & Geometry Constants (mutable â€” P3-02 map load mutates in place)
let showRangeIndicator = true;
const activeProjectiles = [];
const activeMinions = [];
const recentDamageEvents = [];
const testMapConfig = getMapConfig('test_map');
const mapBounds = { ...testMapConfig.bounds };
const bushZones = [{ x: 6, z: -4, radius: 2.8 }, { x: 5, z: 5, radius: 2.2 }];
const impassableZones = [{ type: 'circle', x: -6.5, z: 0.8, radius: 2.8 }];
const wallBoxes = [{ minX: -8.5, maxX: -4.5, minZ: -1.0, maxZ: 2.5 }];

// P3-03: structures + spawners only via mapStructureFactory / matchMapLoad
const boot = bootSandboxStructures('test_map');
let allTurretEntities = boot.turrets;
let redTurretEntity = boot.redTurret;
let blueSpawner = boot.blueSpawner;
let redSpawner = boot.redSpawner;
const bootPlan = boot.plan;

const championEntity = new Entity({ id: 'player-ashe', name: 'Ashe', type: 'champion', team: 'blue', x: -3, y: 0, z: -2, radius: 20, ms: 325, attackRange: 600, as: 0.658, ad: 59, hp: 570, maxHp: 570 });
championEntity.championKey = 'ashe';
const playerInventory = new InventorySystem(championEntity, 6);
const garenEntity = new Entity({ id: 'enemy-garen', name: 'Garen', type: 'champion', team: 'red', x: 3, y: 0, z: 3, radius: 24, ms: 340, attackRange: 175, ad: 69, hp: 620, maxHp: 620 });
const dummyEntity = new Entity({ id: 'training-dummy', name: 'Dummy', type: 'champion', team: 'red', x: 6, y: 0, z: -4, radius: 24, ms: 0, attackRange: 0, ad: 0, hp: 10000, maxHp: 10000, armor: 30, mr: 30, bounty: 0 });
dummyEntity.isPassive = true;

// P3-01/03: single match entity source of truth; structures from factory
const matchRuntime = new MatchRuntime({ mapId: 'test_map' });
matchRuntime.applyMapLoad(bootPlan);
matchRuntime.seedSandbox({
  player: championEntity,
  champions: [garenEntity, dummyEntity],
  structures: allTurretEntities
});

// UI Controllers & Floating Damage Text Setup
const floatingTextUI = new FloatingTextUI(dom.floatingTextContainer, camera);
const shopUI = new ShopUI({
  shopModal: dom.shopModal,
  shopItemsList: dom.shopItemsList,
  shopSearchInput: dom.shopSearchInput,
  shopCategoryRoot: dom.shopCategoryRoot,
  inventorySlots: dom.inventorySlots,
  playerInventory,
  championEntity,
  getGameTime: () => matchRuntime.gameTime || 0
});
const tryToggleShop = createShopToggleHandler({
  getMapId: () => matchRuntime.mapId, getEntity: () => championEntity, shopUI,
  toggleBtn: dom.shopToggleBtn, closeBtn: dom.shopCloseBtn
}).tryToggleShop;
const hudUI = createHudFromDom(dom);

hudUI.applyHpTicks(dom.playerHpBox, championEntity.maxHp, 50);
hudUI.applyHpTicks(dom.garenHpBox, garenEntity.maxHp, 50);

championEntity.onKillCallback = (victim, gold) => {
  if (gold > 0) {
    floatingTextUI.showGoldText(victim.x, victim.z, gold);
  }
};

[championEntity, garenEntity, dummyEntity, ...allTurretEntities].forEach(ent => {
  ent.onDamageCallback = ({ effectiveDamage, damageType, isCrit }) => {
    if (effectiveDamage > 0) {
      floatingTextUI.showDamageText(ent.x, ent.z, effectiveDamage, damageType, isCrit);
    }
  };
});

function addMinionsToScene(minionDomainList) {
  if (!minionDomainList) return;
  minionDomainList.forEach(m => {
    m.onDamageCallback = ({ effectiveDamage, damageType, isCrit }) => {
      if (effectiveDamage > 0) {
        floatingTextUI.showDamageText(m.x, m.z, effectiveDamage, damageType, isCrit);
      }
    };
    const renderObj = MeshFactory.createMinionMesh(m, hudUI, dom.minionsUiContainer);
    scene.add(renderObj.group);
    activeMinions.push({ domain: m, ...renderObj });
    if (!matchRuntime.entities.getById(m.id)) {
      matchRuntime.entities.add(m);
    }
  });
}

// Scene Reticles & Meshes Setup
const targetMarkerMesh = new Mesh(new RingGeometry(0.35, 0.5, 16).rotateX(-Math.PI / 2), new MeshBasicMaterial({ color: 0xffdf00, side: DoubleSide }));
targetMarkerMesh.visible = false;
scene.add(targetMarkerMesh);

const sandboxPropGroups = bootSandboxPropGroups(scene);

const championGroup = new Group();
let currentChampionMeshModel = ChampionMeshFactory.createChampionMesh(championEntity.name || 'ashe', 'blue');
championGroup.add(currentChampionMeshModel);

const attackRangeMesh = new Mesh(new RingGeometry(5.92, 6.0, 64).rotateX(-Math.PI / 2), new MeshBasicMaterial({ color: 0x06b6d4, side: DoubleSide, transparent: true, opacity: 0.7 }));
attackRangeMesh.position.set(0, 0.04, 0);
championGroup.add(attackRangeMesh);

const redBuffAura = createBuffAuraGroup(0xff2222, 0xff3333, 1.05, 1.25, 1.15);
championGroup.add(redBuffAura.group);
const blueBuffAura = createBuffAuraGroup(0x0088ff, 0x38bdf8, 1.65, 1.85, 1.75);
championGroup.add(blueBuffAura.group);
const baronBuffAura = createBuffAuraGroup(0xa855f7, 0xfacc15, 2.05, 2.25, 2.15);
championGroup.add(baronBuffAura.group);
scene.add(championGroup);

const garenGroup = new Group();
garenGroup.add(ChampionMeshFactory.createGarenMesh('red'));
garenGroup.position.set(3, 0, 3);
scene.add(garenGroup);

const dummyGroup = new Group();
dummyGroup.add(ChampionMeshFactory.createDummyMesh());
dummyGroup.position.set(6, 0, -4);
scene.add(dummyGroup);

const dummyPlateDiv = document.createElement('div');
dummyPlateDiv.className = 'entity-plate';
dummyPlateDiv.innerHTML = `<div class="plate-name" style="color: #94a3b8;">Dummy de Entrenamiento</div><div id="dummy-hp-box" class="plate-hp-outer"><div id="dummy-hp-bar" class="enemy-hp-inner"></div></div>`;
document.getElementById('ui-overlay').appendChild(dummyPlateDiv);
const dummyHpBar = document.getElementById('dummy-hp-bar');
hudUI.applyHpTicks(document.getElementById('dummy-hp-box'), dummyEntity.maxHp, 500);

let turretRenderObjects = allTurretEntities.map(t => ({ entity: t, ...MeshFactory.createTurretMesh(t, hudUI, dom.turretsUiContainer) }));
turretRenderObjects.forEach(tr => scene.add(tr.group));

const switchChampion = createSwitchChampionController({
  championEntity,
  championGroup,
  getMesh: () => currentChampionMeshModel,
  setMesh: (m) => { currentChampionMeshModel = m; },
  dom,
  hudUI
});

let inputController = null;
const abilityCasterVfx = createAbilityCasterVfx({ THREE, scene });
wireGroundZoneOpacityDebug();
const abilitySfx = bootAbilitySfx();
const abilityCast = createPlayerAbilityCastController({
  getChampion: () => championEntity,
  getMouse: () => (inputController
    ? inputController.currentMouseWorld
    : { x: championEntity.x, z: championEntity.z }),
  getEntities: () => matchRuntime.getDamageableTargets({
    minionDomains: activeMinions.map((m) => m.domain)
  }),
  getGameTime: () => matchRuntime.gameTime || 0,
  scene,
  activeProjectiles,
  THREE,
  playCasterVfx: (vfxId, caster, opts) => abilityCasterVfx.play(vfxId, caster, opts),
  playAbilitySfx: (vfxId, kind) => abilitySfx.playForCastResult(vfxId, kind),
  playAbilityHitSfx: (vfxId) => abilitySfx.playHit(vfxId)
});
wireAbilityCastDebug(abilityCast);

inputController = new InputController({
  domElement: renderer.domElement, camera, groundPlane: new Plane(new Vector3(0, 1, 0), 0),
  championEntity, getDamageableTargetsCallback: () => matchRuntime.getDamageableTargets({
    minionDomains: activeMinions.map((m) => m.domain)
  }),
  getActiveJungleMonstersCallback: () => activeJungleCamps.flatMap(c => (c.state.isAlive && c.state.monsters) ? c.state.monsters.filter(m => !m.isDead) : []),
  targetMarkerMesh, mapBounds, bushZones, wallBoxes,
  getActiveWardsCallback: () => matchRuntime.activeWards || [],
  onActivateQ: abilityCast.onActivateQ,
  onCastW: abilityCast.onCastW,
  onCastE: abilityCast.onCastE,
  onCastR: abilityCast.onCastR,
  onToggleShop: () => tryToggleShop(),
  onInspectEntity: (entity) => entityInspect.setEntity(entity),
  onRecallAttempt: createRecallInputWire({ getMapId: () => matchRuntime.mapId }).onRecallAttempt
});
inputController.init();

const { summonerSpellWire, applyLobbySpells, tick: tickSummonerSpells } = bootSummonerSpells({
  getCaster: () => championEntity,
  getCursor: () => inputController.currentMouseWorld,
  getAllies: () => [championEntity],
  inputController,
  hudUI,
  THREE,
  scene
});

// Jungle Camps Management
let activeJungleCamps = [];
const spawnJungleCamps = createJungleSpawnHandler({
  getMapId: () => matchRuntime.mapId,
  getLastPlanCamps: () => matchRuntime.lastPlan?.jungleCamps,
  getMapConfigCamps: () => getMapConfig(matchRuntime.mapId).jungleCamps,
  scene,
  hudUI,
  uiContainer: dom.minionsUiContainer,
  getActiveJungleCamps: () => activeJungleCamps,
  setActiveJungleCamps: (next) => { activeJungleCamps = next; }
});
spawnJungleCamps();

function attachStructureDamageCallbacks(entities) {
  entities.forEach((ent) => {
    ent.onDamageCallback = ({ effectiveDamage, damageType, isCrit }) => {
      if (effectiveDamage > 0) {
        floatingTextUI.showDamageText(ent.x, ent.z, effectiveDamage, damageType, isCrit);
      }
    };
  });
}

// Debug Panel Controller Initialization
const mapSceneState = { brushGroups: [], aramDispose: null, fowClearRoot: null };
const {
  matchOutcome: matchOutcomeWire,
  debugPanelController,
  matchEndUi
} = bootDebugTools({
  championEntity, garenEntity, dummyEntity, blueSpawner, redSpawner, floatingTextUI, shopUI,
  spawnJungleCamps, getActiveJungleCamps: () => activeJungleCamps, switchChampion, matchRuntime,
  inputController,
  wallBoxes,
  bushZones,
  getMapSceneState: () => mapSceneState,
  getAlliedViewers: () => [
    ...activeMinions.map((m) => m.domain),
    garenEntity
  ].filter(Boolean)
});

const entityInspect = createEntityInspectController();
bootMapLobbyLoop({
  scene,
  entityInspect,
  loadLiveMapDeps: {
    mapBounds, bushZones, wallBoxes, impassableZones,
    getTurretRenderObjects: () => turretRenderObjects,
    matchRuntime, hudUI,
    turretsUiContainer: dom.turretsUiContainer,
    jungleUiContainer: dom.minionsUiContainer,
    championEntity,
    secondaryChampions: [garenEntity, dummyEntity],
    attachDamageCallbacks: attachStructureDamageCallbacks,
    getActiveJungleCamps: () => activeJungleCamps,
    setActiveJungleCamps: (next) => { activeJungleCamps = next; },
    gridHelper, _mapSceneState: mapSceneState,
    onTurretsRebuilt: (turrets, renders) => {
      allTurretEntities = turrets;
      redTurretEntity = turrets.find((t) => t.team === 'red') || turrets[1] || null;
      turretRenderObjects = renders;
    },
    onSpawnersRebuilt: (spawners) => {
      blueSpawner = spawners.find((s) => s.team === 'blue') || spawners[0] || null;
      redSpawner = spawners.find((s) => s.team === 'red') || spawners[1] || null;
      debugPanelController.blueSpawner = blueSpawner;
      debugPanelController.redSpawner = redSpawner;
    },
    onPlayerPlaced: (entity) => inputController.cameraController.snapTo(entity),
    garenGroup, garenPlate: dom.garenPlate, dummyGroup, dummyPlateDiv, sandboxPropGroups
  },
  afterLobbyDeps: {
    dom, scene, THREE, matchRuntime, mapBounds, switchChampion, applyLobbySpells,
    championEntity, playerInventory, matchEndUi, matchOutcomeWire, entityInspect,
    activeMinions, activeProjectiles, activeJungleCamps,
    getBlueSpawner: () => blueSpawner, getRedSpawner: () => redSpawner,
    wallBoxes, bushZones, impassableZones, recentDamageEvents,
    addMinionsToScene, MeshFactory, dummyEntity, tickSummonerSpells, abilityCasterVfx,
    mapSceneState, abilityCast, summonerSpellWire, inputController,
    onToggleRange: () => {
      showRangeIndicator = !showRangeIndicator;
      attackRangeMesh.visible = showRangeIndicator;
    },
    championGroup, hudUI,
    getRedTurretEntity: () => redTurretEntity,
    garenEntity, garenGroup, dummyPlateDiv, dummyHpBar, dummyGroup,
    getTurretRenderObjects: () => turretRenderObjects, camera, renderer
  }
});
